<script lang="ts">
  import { onMount } from 'svelte';
  import { read, utils } from 'xlsx';
  import { animate } from '@motionone/dom';
  import { inView } from '@motionone/dom';
  import TagFilter from './components/TagFilter.svelte';
  import { reveal } from 'svelte-reveal';
  import Quote from '$lib/components/Quote.svelte';
  
  // Meta data for sharing
  const metaData = {
    title: 'Jonas Loh - Creative Lead',
    description: 'Creative Lead',
    image: '/og/preview.jpg', // This will be generated from our OG route
    url: 'https://jonasloh.com' // Replace with your actual URL
  };
  
  // Define CV item interface
  interface CVItem {
    year: string;
    type: string;
    title: string;
    affiliation: string;
  }
  
  // Type mapping for plural forms
  const typeMapping: Record<string, string> = {
    'Exhibition': 'Exhibitions',
    'Position': 'Roles',
    'Publication': 'Publications',
    'Academic': 'Academic'
  };
  
  // CV data state
  let cvItems: CVItem[] = [];
  let filteredItems: CVItem[] = [];
  let loading = true;
  let error = false;
  let errorMessage = '';
  
  // Sorting and filtering state
  let sortDirection: 'asc' | 'desc' = 'desc'; // Default sort newest first
  let selectedType: string | null = 'Roles';
  let availableTypes: string[] = ['Roles'];
  
  let cvSection: HTMLElement;
  
  // Function to format date string to just year
  function formatYear(dateString: string): string {
    const yearMatch = dateString.match(/\d{4}/);
    return yearMatch ? yearMatch[0] : dateString;
  }
  
  // Function to map old type to new plural form
  function mapType(type: string): string {
    return typeMapping[type] || type;
  }
  
  // Function to fetch CV data from a published Google Sheet
  async function fetchCVData() {
    try {
      loading = true;
      error = false;
      errorMessage = '';
      
      // Use CSV format with the correct sheet ID
      const sheetId = "1D9p4I3z3ZlRo0VaO7gVonaSLWWIk4WmUtTuCEDCEjng";
      const sheetURL = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      console.log('Starting CV data fetch...');
      console.log('URL:', sheetURL);
      
      const response = await fetch(sheetURL, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'text/csv'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch spreadsheet: ${response.status} ${response.statusText}`);
      }
      
      const csvText = await response.text();
      
      if (!csvText.trim()) {
        throw new Error('Received empty CSV data');
      }
      
      const rows = csvText.split('\n').map(row => {
        const values: string[] = [];
        let currentValue = '';
        let insideQuotes = false;
        
        for (let i = 0; i < row.length; i++) {
          const char = row[i];
          
          if (char === '"') {
            if (row[i + 1] === '"') {
              currentValue += '"';
              i++;
            } else {
              insideQuotes = !insideQuotes;
            }
          } else if (char === ',' && !insideQuotes) {
            values.push(currentValue.trim());
            currentValue = '';
          } else {
            currentValue += char;
          }
        }
        
        values.push(currentValue.trim());
        return values;
      });
      
      cvItems = rows.slice(1)
        .filter(row => {
          if (row.length < 5) return false;
          const showValue = row[4]?.toLowerCase().trim();
          return showValue === 'true' || showValue === 'checked' || showValue === 'x' || showValue === '✓' || showValue === 't';
        })
        .map(row => ({
          year: row[0].trim(),
          type: mapType(row[1].trim()),
          title: row[2].trim(),
          affiliation: row[3].trim()
        }));
      
      // Extract unique types for the filter dropdown
      const types = [...new Set(cvItems.map(item => item.type))].sort();
      availableTypes = ['Roles', ...types.filter(type => type !== 'Roles' && type !== 'All')];
      
      // Apply initial sort and filter
      sortAndFilterItems();
      
      loading = false;
    } catch (error: unknown) {
      console.error('Error loading CV data:', error);
      if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Unknown error occurred';
      }
      error = true;
      loading = false;
    }
  }
  
  // Function to sort and filter items
  function sortAndFilterItems() {
    //console.log('Sorting and filtering items');
    //console.log('Selected type:', selectedType);
    //console.log('Total CV items:', cvItems.length);
    
    // First filter by type
    let items = selectedType === 'All' 
      ? [...cvItems] 
      : cvItems.filter(item => item.type === selectedType);
    
    //console.log('Filtered items:', items.length);
    
    // Then sort by year - make sure to handle date strings properly
    items.sort((a, b) => {
      const yearA = parseInt(formatYear(a.year)) || 0;
      const yearB = parseInt(formatYear(b.year)) || 0;
      
      return sortDirection === 'asc' 
        ? yearA - yearB 
        : yearB - yearA;
    });
    
    //console.log('Final sorted items:', items.length);
    filteredItems = items;
  }
  
  // Function to toggle sort direction
  function toggleSort() {
    sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    sortAndFilterItems();
  }
  
  // Function to handle type filter change
  function handleTypeChange(event: Event) {
    event.preventDefault();
    selectedType = (event.target as HTMLSelectElement).value;
    sortAndFilterItems();
  }
  
  function handleTypeSelect(type: string | null) {
    selectedType = type;
    sortAndFilterItems();
  }
  
  onMount(() => {
    // Fetch CV data when component mounts
    fetchCVData();
  });
</script>


<!-- CV Section - using the same structure as projects section -->
<div class="cv-section w-full">



  <div class="container mt-16 mx-auto px-2 md:px-4 max-w-5xl lg:max-w-6xl xl:max-w-[1600px] 2xl:max-w-[1920px]">
    <div class="mb-12" use:reveal={{ y: 40 }}>
      <h2 class="text-2xl animate-on-view mb-4">Selected Experience</h2>
      <p class="animate-on-view">

        I've delivered high-impact, complex products across AI, data, and UX. My work has been exhibited at MoMA New York and the Vienna Architecture Biennale. I'm a patent holder and have taught at leading design universities in Germany.
      </p>
    </div>
    
    <div class="max-w-7xl mx-auto">
      {#if loading}
        <p class="text-gray-600">Loading CV data...</p>
      {:else if error}
        <div class="bg-red-50 border border-red-200 rounded-sm p-4 mb-8">
          <p class="text-red-700 mb-2">Error loading CV data</p>
          <p class="text-sm text-red-600">{errorMessage}</p>
          <button 
            class="mt-4 px-4 py-2 text-sm bg-red-100 text-red-700 rounded-sm hover:bg-red-200"
            on:click={() => fetchCVData()}
          >
            Retry
          </button>
        </div>
      {:else}
        <!-- Filter Bar -->
        <div class="flex flex-col md:flex-row md:justify-start gap-4 mb-8" use:reveal={{ blur: 0 }}>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-gray-500">Type</label>
            <div class="tag-filter-container">
              {#each availableTypes as type}
                <button
                  class="tag {selectedType === type ? 'tag-selected' : ''}"
                  data-type={type}
                  on:click={() => handleTypeSelect(type)}
                >
                  {type}
                </button>
              {/each}
            </div>
          </div>
        </div>

        <!-- Experience Table -->
        <div class="overflow-x-auto">
          <div class="grid gap-8">
            {#if filteredItems.length === 0}
              <div class="text-center py-8 text-gray-500">
                No experience entries match the selected filter.
              </div>
            {:else}
              {#each filteredItems as item}
                <div class="border-b border-gray-100 pb-8" use:reveal={{ blur: 10 }}>
                  <div class="flex items-center gap-2 mb-1">
                    <div class="text-sm text-gray-500">{formatYear(item.year)}</div>
                    <div class="type-indicator {item.type.toLowerCase()}">{item.type}</div>
                  </div>
                  <div class="text-lg font-semibold mb-1">{item.title}</div>
                  <div class="text-sm text-gray-600">{item.affiliation}</div>
                </div>
              {/each}
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .cv-section {
    position: relative;
    background-color: #FFF;
    padding: 6rem 0 1rem 0;
    z-index: 1;
  }

  .tag-filter-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .tag {
    font-size: 1rem;
    padding: 0.5em 1.2em;
    border-radius: 0.25em;
    min-width: 90px;
    text-align: center;
    box-sizing: border-box;
    margin-bottom: 0.5rem;
    /* Use global background and color for selected */
  }

  @media (max-width: 640px) {
    .tag {
      font-size: 0.95rem;
      padding: 0.4em 0.8em;
      min-width: 70px;
    }
    .tag-filter-container {
      gap: 0.4rem;
    }
  }

  .type-indicator {
    font-size: 0.75rem;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    text-transform: capitalize;
    font-weight: 500;
  }

  .academic {
    background-color: rgba(173, 216, 230, 0.2);
    color: rgb(0, 122, 204);
  }

  .exhibitions {
    background-color: rgba(144, 238, 144, 0.2);
    color: rgb(34, 139, 34);
  }

  .roles {
    background-color: rgba(255, 192, 203, 0.2);
    color: rgb(219, 68, 119);
  }

  .publications {
    background-color: rgba(147, 112, 219, 0.2);
    color: rgb(75, 0, 130);
  }

  /* Mobile optimizations */
  @media (max-width: 640px) {
    .text-lg {
      font-size: 1rem;
    }
    
    .text-sm {
      font-size: 0.875rem;
    }
  }
</style> 